import FullCalendar, {
  CalendarOptions,
  DatesSetArg,
  EventClickArg,
  EventContentArg,
  EventInput,
  EventMountArg,
} from '@fullcalendar/react';
import React, { memo, useCallback, useMemo, useState, FC } from 'react';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import timeGridPlugin from '@fullcalendar/timegrid';
import luxonPlugin, { toLuxonDateTime } from '@fullcalendar/luxon';
import { IntlShape, useIntl } from 'react-intl';
import {
  nextFetchEndDateFC,
  nextFetchStartDate,
  yesterday,
} from '../../../molecules/merchant/calendar/calendarHelper';
import { CalendarLoading } from '../../../molecules/merchant/calendar/CalendarLoading';
import MerchantFullCalendarEventContent from '../../../molecules/merchant/calendar/pc/MerchantFullCalendarEventContent';
import MerchantResourceSummaryModal from '../../../molecules/merchant/calendar/pc/MerchantResourceSummaryModal';
import BookingEvent, {
  FullCalendarViewStyle,
} from '../../../domain/entities/BookingEvent';
import {
  VacancyStatus,
  VacancyStatusEnum,
} from '../../../domain/entities/VacancyStatus';
import {
  mapToCalendarEvents,
  mapToAllDayCalendarEvents,
  useFormatEventTitle,
} from '../../../hooks/fullcalendar';
import { useGetReservationFlowVersion } from 'src/hooks/api/reservation-flow/useGetReservationFlowVersion';
import implFullCalendarRepository from '../../../infrastructure/repositories/FullCalendarEventRepository';
import * as dateFns from 'date-fns';
import { MerchantResourceSummarizeTimeSlotModal } from '../../../molecules/merchant/calendar/pc/MerchantResourceSummarizeTimeSlotModal';
import { EventTypeResourceDetailModalOnCalendar } from 'src/templates/merchants/resources/book/event_type/components/EventTypeResourceDetailModalOnCalendar';
import { CourseTypeResourceDetailModalOnCalendar } from 'src/templates/merchants/resources/book/course_type/components/CourseTypeResourceDetailModalOnCalendar';
import { buildUrl } from 'src/utils/request-url';
const { format, isEqual, startOfDay } = dateFns;

const ViewStyles = [
  FullCalendarViewStyle.dayGridMonth,
  FullCalendarViewStyle.timeGridWeek,
  FullCalendarViewStyle.timeGridThreeDay,
  FullCalendarViewStyle.timeGridDay,
  FullCalendarViewStyle.listMonth,
];

const intlPref = 'merchants.services.merchantFullCalendar.calendar.';

const eventContent = (arg: EventContentArg) => {
  const bookingEvent = arg.event.extendedProps.bookingEvent as BookingEvent;
  const vacancyStatus: VacancyStatus = new VacancyStatus({
    vacancyCount: bookingEvent.vacancy,
    waitingListProvided: bookingEvent.isWaitingListProvided,
    full: bookingEvent.full,
    isRegisteredWaitingList: bookingEvent.isRegisteredWaitingList,
  });

  const shouldSummarizeTimeSlot = arg.event.extendedProps
    .shouldSummarizeTimeSlot as boolean;

  if (
    !shouldSummarizeTimeSlot &&
    vacancyStatus.vacancyAppearance === VacancyStatusEnum.SOLD_OUT
  ) {
    return (
      <MerchantFullCalendarEventContent
        bookingEvent={bookingEvent}
        viewType={arg.view.type}
        textColor={arg.event.backgroundColor}
        title={arg.event.title}
        timeText={arg.timeText}
      />
    );
  }

  // render default component
  return undefined;
};

// 別ファイルに分けると以下のエラーが出るので、同じファイルにFullCalendarを読み込んでいる
// https://stackoverflow.com/questions/62239183/fullcalendar-lib-before-attempting-to-import-a-plugin
const BookingPageFullCalendar = memo<CalendarOptions>((props) => {
  const intl = useIntl();
  return (
    <FullCalendar
      allDayText={intl.formatMessage({
        id: intlPref + 'buttonText.allDay',
      })}
      businessHours={{
        startTime: '00:00',
        endTime: '24:00',
        daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
      }}
      buttonText={{
        today: intl.formatMessage({
          id: intlPref + 'buttonText.today',
        }),
        month: intl.formatMessage({
          id: intlPref + 'buttonText.month',
        }),
        week: intl.formatMessage({
          id: intlPref + 'buttonText.week',
        }),
        day: intl.formatMessage({ id: intlPref + 'buttonText.day' }),
        list: intl.formatMessage({
          id: intlPref + 'buttonText.list',
        }),
      }}
      editable={true}
      eventDisplay={'block'}
      eventTimeFormat={{
        hour: 'numeric',
        minute: '2-digit',
        meridiem: false,
        hour12: false,
      }}
      fixedWeekCount={false}
      headerToolbar={{
        left: 'today',
        center: 'prev title next',
        right: ViewStyles.join(),
      }}
      initialView={FullCalendarViewStyle.dayGridMonth}
      locale={intl.locale}
      plugins={[dayGridPlugin, timeGridPlugin, listPlugin, luxonPlugin]}
      views={{
        timeGridThreeDay: {
          type: 'timeGrid',
          duration: { days: 3 },
          buttonText: intl.formatMessage({
            id: intlPref + 'buttonText.3days',
          }),
        },
      }}
      eventContent={eventContent}
      {...props}
    />
  );
});

type FilterFunction = (bookingEvents: EventInput[]) => EventInput[];

type Props = {
  merchantPublicId: string;
  merchantTimezone: string;
  filterBookingPagesFunctions?: FilterFunction[];
  intl: IntlShape;
  baseDate: string;
};

export const BookingPagesFullCalendarOrganism: FC<Props> = ({
  merchantPublicId,
  merchantTimezone,
  filterBookingPagesFunctions = [],
  baseDate,
}) => {
  const intl = useIntl();
  const [fetchUntil, setFetchUntil] = useState<string>(
    yesterday(merchantTimezone).toISO(),
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [events, setEvents] = useState<EventInput[]>([]);
  const [rowEvents, setRowEvents] = useState<BookingEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<BookingEvent | undefined>(
    undefined,
  );

  // time_slotを1日毎にまとめて表示するか
  const shouldSummarizeTimeSlot = merchantPublicId === 'hiramekistudio-spark';

  const formatEventTitle = useFormatEventTitle(intl);

  const toggleSummaryModal = useCallback<(event?: BookingEvent) => void>(
    (event) => {
      setSelectedEvent(event);
    },
    [],
  );

  const startBookingFlow = useCallback((event: BookingEvent) => {
    // /reserve/{sitePublicId}/{bookingServicePubId}?selected_date=2020-11-15
    location.href = buildUrl(`${event.bookingActionUrl}#pageContent`);
  }, []);

  const fetchServices = useCallback<
    (params: { startStr: string; endStr: string }) => void
  >(
    async ({ startStr, endStr }) => {
      setIsLoading(true);
      const bookingEventsResponse = await implFullCalendarRepository.Search(
        merchantPublicId,
        startStr,
        endStr,
      );

      const calendarEvents = shouldSummarizeTimeSlot
        ? // 該当のmerchantの場合time_slotをまとめて表示する
          mapToAllDayCalendarEvents(
            Array.from(
              new Map(
                bookingEventsResponse.events.map((event) => {
                  return [
                    event.bookingPageId +
                      format(startOfDay(event.start), 'YYYYMMDDHHmm'),
                    event,
                  ];
                }),
              ).values(),
            ),
          )
        : mapToCalendarEvents(bookingEventsResponse.events);

      setRowEvents([...rowEvents, ...bookingEventsResponse.events]);
      setEvents([...events, ...calendarEvents]);
      setFetchUntil(endStr);
      setIsLoading(false);
    },
    [rowEvents, events, shouldSummarizeTimeSlot, merchantPublicId],
  );

  const handleDatesSet = useCallback(
    (arg: DatesSetArg) => {
      const newFetchUntil = nextFetchStartDate(fetchUntil);
      const end = nextFetchEndDateFC(
        toLuxonDateTime(arg.end, arg.view.calendar),
      );

      if (end > newFetchUntil) {
        fetchServices({
          startStr: newFetchUntil.toISO(),
          endStr: end.toISO(),
        });
      }
    },
    [fetchUntil, fetchServices],
  );

  const handleEventDidMount = useCallback(
    (arg: EventMountArg) => {
      const bookingEvent = arg.event.extendedProps.bookingEvent as BookingEvent;
      const isShorten = arg.view.type !== FullCalendarViewStyle.listMonth;
      arg.event.setProp(
        'title',
        formatEventTitle(bookingEvent, isShorten, shouldSummarizeTimeSlot),
      );
    },
    [formatEventTitle, shouldSummarizeTimeSlot],
  );

  const handleEventClick = useCallback(
    (arg: EventClickArg) => {
      const event = arg.event.extendedProps.bookingEvent as BookingEvent;

      event.isPasswordProtected
        ? startBookingFlow(event)
        : toggleSummaryModal(event);
    },
    [startBookingFlow, toggleSummaryModal],
  );
  // note
  const filteredEvents = useMemo<EventInput[]>(() => {
    let result = events;
    filterBookingPagesFunctions.forEach((filterFn) => {
      const filtered = filterFn(result);
      result = filtered;
    });
    return result;
  }, [filterBookingPagesFunctions, events]);

  const sameDayEvents = useMemo(
    () =>
      rowEvents.filter(
        (e) =>
          e.bookingPageId === selectedEvent?.bookingPageId &&
          isEqual(startOfDay(e.start), startOfDay(selectedEvent.start)),
      ),
    [rowEvents, selectedEvent?.bookingPageId, selectedEvent?.start],
  );

  const isEventType = (event: BookingEvent) =>
    !!(event.modelType === 'EVENT_SCHEME');
  const isCourseType = (event: BookingEvent) =>
    !!(
      event.modelType === 'AVAILABILITY_SCHEME' ||
      event.modelType === 'ASSIGN_SCHEME'
    );

  const { data, isLoading: isReservationFlowVersionLoading } =
    useGetReservationFlowVersion(
      merchantPublicId,
      selectedEvent?.bookingPageId,
    );

  const isV2Flow = (event: BookingEvent) => {
    const isV2 = data?.reservation_flow_version === 'v2';

    return !!(isV2 && (isEventType(event) || isCourseType(event)));
  };

  const eventTypeNewReservationFlowPath = (() => {
    if (!selectedEvent) return undefined;
    const [pathname, searchParams] = selectedEvent.bookingActionUrl.split('?');
    return `${pathname}/book/event_type?${searchParams.replace(
      'selected_slot',
      'timeSlotId',
    )}`;
  })();

  const courseTypeNewReservationFlowPath = (() => {
    if (!selectedEvent) return undefined;
    const [pathname, searchParams] = selectedEvent.bookingActionUrl.split('?');
    return `${pathname}/book/course_type?${searchParams.replace(
      'selected_date',
      'date',
    )}`;
  })();

  return (
    <>
      <BookingPageFullCalendar
        initialDate={baseDate}
        timeZone={merchantTimezone}
        datesSet={handleDatesSet}
        events={filteredEvents}
        eventDidMount={handleEventDidMount}
        eventClick={handleEventClick}
      />
      {selectedEvent &&
        shouldSummarizeTimeSlot &&
        !isV2Flow(selectedEvent) &&
        !isReservationFlowVersionLoading && (
          <MerchantResourceSummarizeTimeSlotModal
            sameDayEvents={sameDayEvents}
            event={selectedEvent}
            merchantId={merchantPublicId}
            handleClose={() => toggleSummaryModal()}
            handleBook={startBookingFlow}
          />
        )}
      {selectedEvent &&
        !shouldSummarizeTimeSlot &&
        !isV2Flow(selectedEvent) &&
        !isReservationFlowVersionLoading && (
          <MerchantResourceSummaryModal
            event={selectedEvent}
            merchantId={merchantPublicId}
            handleClose={() => toggleSummaryModal()}
            handleBook={startBookingFlow}
          />
        )}
      {selectedEvent &&
        isV2Flow(selectedEvent) &&
        isEventType(selectedEvent) && (
          <EventTypeResourceDetailModalOnCalendar
            isOpen={!!selectedEvent}
            onRequireClosing={() => toggleSummaryModal()}
            merchantPublicId={merchantPublicId}
            resourcePublicId={selectedEvent?.bookingPageId}
            reservationPath={eventTypeNewReservationFlowPath}
            isTimeSlotFull={selectedEvent?.full ?? undefined}
            start={selectedEvent?.start}
            end={selectedEvent?.end}
            vacancy={selectedEvent?.vacancy ?? undefined}
            isRegisteredWaitingList={selectedEvent?.isRegisteredWaitingList}
          />
        )}
      {selectedEvent &&
        isV2Flow(selectedEvent) &&
        isCourseType(selectedEvent) && (
          <CourseTypeResourceDetailModalOnCalendar
            isOpen={!!selectedEvent}
            onRequireClosing={() => toggleSummaryModal()}
            merchantPublicId={merchantPublicId}
            resourcePublicId={selectedEvent?.bookingPageId}
            reservationPath={courseTypeNewReservationFlowPath}
          />
        )}
      {(isLoading || isReservationFlowVersionLoading) && <CalendarLoading />}
    </>
  );
};
